import React, { createContext, FC, useCallback, useMemo, useRef, useState } from 'react';
import { RampInstantSDK } from '@ramp-network/ramp-instant-sdk';
import config from 'config/config';
import { userPaymentAccountsSelector } from 'modules/payments/store/user-payment-accounts/selectors';
import { useSelector } from 'react-redux';
import { calculateSuggestedAmountToBuy } from 'shared/utils/add-funds-utils';
import { CurrencyType } from '@okletsplay/core';
import { exchangeRatesSelector } from 'modules/payments/store/exchange-rates/selectors';
import { isFunction } from 'lodash';
import { RampWidgetConfig } from './interfaces';
import { DEFAULT_WIDGET_CONFIG, IS_RAMP_WIDGET_ENABLED } from './constants';
import { calculateSuggestedTokensAmount } from './utils/calculate-suggested-tokens-amount';

type OpenWidgetOptions =
  | {
      originalAmount: number;
      platformFeePercentage: number;
      currencyType: CurrencyType;
    }
  | undefined;

export const RampWidgetContext = createContext<{
  openWidget: (openOptions?: OpenWidgetOptions) => void;
  isLoading: boolean;
  isEnabled: boolean;
}>({
  openWidget: () => {},
  isLoading: false,
  isEnabled: false,
});

export const RampWidgetProvider: FC = ({ children }) => {
  const widget = useRef<undefined | RampInstantSDK>();

  const [isLoading, setLoading] = useState(false);

  const balanceDepositAddress = useSelector(userPaymentAccountsSelector.depositAddress);
  const userAccounts = useSelector(userPaymentAccountsSelector.all);
  const exchangeRates = useSelector(exchangeRatesSelector.all);

  const options = useMemo(() => {
    const result: RampWidgetConfig = {
      ...DEFAULT_WIDGET_CONFIG,
    };

    if (balanceDepositAddress) {
      result.userAddress = balanceDepositAddress;
    }

    if (config.rampWebhookStatusUrl) {
      result.webhookStatusUrl = config.rampWebhookStatusUrl;
    }

    return result;
  }, [balanceDepositAddress]);

  const getConfig = useCallback(
    async (openOptions: OpenWidgetOptions): Promise<RampWidgetConfig> => {
      const rampConfig: RampWidgetConfig = { ...options };

      if (
        userAccounts.length &&
        openOptions?.originalAmount &&
        openOptions?.platformFeePercentage &&
        openOptions?.originalAmount != 0 &&
        openOptions?.currencyType
      ) {
        const suggestedAmount = calculateSuggestedAmountToBuy({
          mainAmount: openOptions.originalAmount,
          userAccounts,
          exchangeRates,
          gameActivityFeePercentage: openOptions.platformFeePercentage,
          currencyType: openOptions.currencyType,
        });

        if (suggestedAmount <= 0) {
          return rampConfig;
        }

        setLoading(true);
        try {
          const suggestedTokensAmount = await calculateSuggestedTokensAmount(suggestedAmount);

          if (suggestedTokensAmount !== '0') {
            rampConfig.swapAmount = suggestedTokensAmount;
          }
        } catch (e) {
          console.error(e);
        } finally {
          setLoading(false);
        }
      }

      return rampConfig;
    },
    [options, userAccounts, exchangeRates]
  );

  const openWidget = useCallback(
    async (openOptions?: OpenWidgetOptions) => {
      if (isLoading) return;
      if (!IS_RAMP_WIDGET_ENABLED) return;

      if (isFunction(widget.current?.close)) {
        widget.current?.close();
      }

      const rampConfig = await getConfig(openOptions);

      const newWidget = new RampInstantSDK(rampConfig);
      newWidget.show();
      widget.current = newWidget;
    },
    [getConfig, isLoading]
  );

  return (
    <RampWidgetContext.Provider
      value={{ openWidget, isLoading, isEnabled: IS_RAMP_WIDGET_ENABLED }}
    >
      {children}
    </RampWidgetContext.Provider>
  );
};
